import { useState } from 'react';
import { useLang, useT } from '../../i18n';
import { dayMonthYear } from '../listFormat';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { Message, ReportPage } from './ReportParts';
import { countText, moneyText, supplierName } from './reportFormat';
import { downloadReport, downloadReportWithReason } from './reportsApi';
import { useReport } from './useReport';

// Leo's "Archive" (updated board) = order valorization (B15): the orders of a year or a month by ORDER date, one table per supplier with a total row; pick the year, Full year or a month, All
// suppliers or one; PDF, Excel and CSV for what is shown. Commission here is order value x rate (what is earned stays on Commissions). From 2026: the earlier years are not imported yet, so
// they are not offered. A customer the data does not have yet is a dash. Below, the export for the accountant: the same orders with their Done date and close reason, and the invoice files.
const FIRST_YEAR = 2026;
const sel = { height: 44, borderRadius: 10, border: `1px solid ${L.border}`, background: '#FFFFFF', padding: '0 12px', font: 'inherit', fontSize: 15, color: L.text, minWidth: 130 };
const btn = { height: 44, padding: '0 18px', borderRadius: 10, font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#FFFFFF', color: L.text, border: `1px solid ${L.border}` };
const th = { textAlign: 'left', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: L.muted, fontWeight: 600, padding: '10px 10px', borderBottom: `1px solid ${L.border}` };
const td = { padding: '10px 10px', borderBottom: `1px solid ${L.rowLine}` };
const R = { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

export default function ArchiveView() {
  const t = useT();
  const { lang } = useLang();
  const phone = useViewportKind() === 'phone';
  const now = new Date();
  const [year, setYear] = useState(Math.max(FIRST_YEAR, now.getFullYear()));
  const [month, setMonth] = useState('');                       // '' = the full year
  const [supplier, setSupplier] = useState('');
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);
  const [accBusy, setAccBusy] = useState(null);
  const [accError, setAccError] = useState(null);
  const kind = month ? 'month' : 'year';
  const key = month ? `${year}-${month}` : String(year);
  const { status, data, retry } = useReport('archive-orders', { kind, key });
  const years = Array.from({ length: Math.max(1, Math.max(FIRST_YEAR, now.getFullYear()) - FIRST_YEAR + 1) }, (_, i) => FIRST_YEAR + i);

  const groups = data ? data.groups.filter((g) => !supplier || g.supplier_id === supplier) : [];
  const download = async (format) => {
    setBusy(format); setFailed(false);
    const ok = await downloadReport('archive-orders-file', { kind, key, format, ...(supplier ? { supplier } : null) }, `floxir-orders-${key}.${format}`);
    setBusy(null);
    if (!ok) setFailed(true);
  };
  const accountant = async (what) => {
    setAccBusy(what); setAccError(null);
    const zip = what === 'zip';
    const r = await downloadReportWithReason(zip ? 'accountant-invoices' : 'accountant-export', zip ? { kind, key } : { kind, key, format: what }, zip ? `floxir-invoices-${key}.zip` : `floxir-accountant-${key}.${what}`);
    setAccBusy(null);
    if (!r.ok) setAccError(r.error === 'choose_shorter_period' ? 'reports.accountant.shorter' : 'reports.accountant.failed');
  };
  const title = (g) => `${supplierName(g.supplier, t)} · ${month ? `${year}-${month}` : year}`;

  return (
    <ReportPage title={t('reports.archive.title')} backTo="/reports" testid="report-archive">
      <div data-testid="archive-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <select aria-label={t('reports.orders.year')} data-testid="archive-year" value={year} onChange={(e) => setYear(Number(e.target.value))} style={sel}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select aria-label={t('reports.orders.month')} data-testid="archive-month" value={month} onChange={(e) => setMonth(e.target.value)} style={sel}>
            <option value="">{t('reports.archive.wholeYear')}</option>
            {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select aria-label={t('reports.archive.supplier')} data-testid="archive-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} style={{ ...sel, minWidth: 200 }}>
            <option value="">{t('reports.archive.allSuppliers')}</option>
            {data && data.groups.map((g) => <option key={g.supplier_id || 'none'} value={g.supplier_id || ''}>{supplierName(g.supplier, t)}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['pdf', 'xlsx', 'csv'].map((f) => <button key={f} type="button" disabled={busy === f} onClick={() => download(f)} data-testid={`archive-export-${f}`} style={{ ...btn, opacity: busy === f ? 0.6 : 1 }}>{t(`reports.accountant.${f}`)}</button>)}
        </div>
      </div>
      {failed && <span role="alert" data-testid="archive-export-failed" style={{ fontSize: 14, color: '#9E2A1C' }}>{t('reports.forecast.exportFailed')}</span>}

      {status !== 'ready' ? <Message status={status} onRetry={retry} /> : (
        <>
          {groups.length === 0 && <p data-testid="orders-empty" style={{ color: L.muted }}>{t('reports.archive.noDataYet')}</p>}
          {groups.map((g) => (
            <section key={g.supplier_id || 'none'} data-testid={`orders-group-${g.supplier_id}`} style={{ background: L.card, border: `1px solid ${L.border}`, borderRadius: 20, padding: phone ? '16px 14px' : '24px 28px', overflowX: 'auto' }}>
              <h2 style={{ margin: '0 0 12px', fontFamily: L.serif, fontSize: 23, fontWeight: 500 }}>{title(g)}</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                <thead><tr>
                  <th style={th}>{t('reports.forecast.col.date')}</th><th style={th}>{t('reports.orders.col.order')}</th><th style={th}>{t('reports.forecast.col.customer')}</th><th style={th}>{t('reports.orders.col.supplier')}</th>
                  <th style={{ ...th, ...R }}>{t('reports.orders.col.quantity')}</th><th style={{ ...th, ...R }}>{t('reports.col.rate')}</th><th style={{ ...th, ...R }}>{t('reports.forecast.col.value')}</th><th style={{ ...th, ...R }}>{t('reports.forecast.col.commission')}</th>
                </tr></thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.order_id} data-testid="orders-row">
                      <td style={td}>{dayMonthYear(r.date, lang) || r.date}</td><td style={{ ...td, color: L.accent, fontWeight: 600 }}>{r.number || '—'}</td><td style={td}>{r.customer || '—'}</td><td style={td}>{r.supplier || '—'}</td>
                      <td style={{ ...td, ...R }}>{r.quantity == null ? '—' : countText(r.quantity)}</td><td style={{ ...td, ...R }}>{r.rate == null ? '—' : `${r.rate}%`}</td>
                      <td style={{ ...td, ...R }}>{moneyText(r.value, data.currency)}</td><td style={{ ...td, ...R }}>{moneyText(r.commission, data.currency)}</td>
                    </tr>
                  ))}
                  <tr data-testid={`orders-total-${g.supplier_id}`} style={{ fontWeight: 700 }}>
                    <td style={td} colSpan={4}>{t('reports.orders.total')} {supplierName(g.supplier, t)} · {countText(g.total.orders)}</td><td style={{ ...td, ...R }}>{countText(g.total.quantity)}</td><td style={td} />
                    <td style={{ ...td, ...R }}>{moneyText(g.total.value, data.currency)}</td><td style={{ ...td, ...R }}>{moneyText(g.total.commission, data.currency)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          ))}
          {groups.length > 1 && <p data-testid="orders-grand" style={{ margin: 0, fontWeight: 700 }}>{t('reports.orders.grand')}: {moneyText(groups.reduce((s, g) => s + g.total.value, 0), data.currency)} · {moneyText(groups.reduce((s, g) => s + g.total.commission, 0), data.currency)}</p>}
        </>
      )}

      <div data-testid="accountant-export" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '12px 0', borderTop: `1px solid ${L.rowLine}` }}>
        <strong style={{ fontSize: 14, marginRight: 6 }}>{t('reports.accountant.title')}</strong>
        {['pdf', 'xlsx', 'csv', 'zip'].map((f) => <button key={f} type="button" disabled={accBusy === f} onClick={() => accountant(f)} data-testid={`accountant-${f}`} style={{ ...btn, height: 36, fontSize: 13, opacity: accBusy === f ? 0.6 : 1 }}>{t(`reports.accountant.${f}`)}</button>)}
        <span style={{ fontSize: 13, color: L.muted }}>{t('reports.accountant.help')}</span>
        {accError && <span role="alert" data-testid="accountant-error" style={{ fontSize: 14, color: '#9E2A1C' }}>{t(accError)}</span>}
      </div>
    </ReportPage>
  );
}
